import store, { getChat, setChat, getMyUser } from '@/store'
import { fetchBubble } from '@/api/chat'
import Pusher from 'pusher-js'
import { createLogger } from '@/helpers/log'
import app from '@/app'
import posthog from 'posthog-js'
const log = createLogger('Pusher')

const isComplete = ({ final, chunks }) => {
	// checks to make sure every expected chunk has been received
	if (!final) return false
	for (let i = 0; i < chunks.length; i++) {
		if (!chunks[i]) return false
	}
	return true
}

export const collectChunks = (callback) => {
	const events = {}
	return ({ id, index, chunk, final }) => {
		if (!events[id]) {
			events[id] = { chunks: [], final: false }
		}
		events[id].chunks[index] = chunk
		events[id].final = events[id].final || final
		if (isComplete(events[id])) {
			try {
				const json = events[id].chunks.join('')
				callback(JSON.parse(json))
			} catch (error) {
				log('chunk error', error)
			} finally {
				delete events[id]
			}
		}
	}
}

export const bindEventWithChunking = (event, handler) => {
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher.unbind(event)
	pusher.bind(event, handler)
	const chunkedEvent = `${event}-chunked`
	pusher.unbind(chunkedEvent)
	pusher.bind(chunkedEvent, collectChunks(handler))
}

export const bindEvents = (prefix, eventsByName) => {
	Object.entries(eventsByName).forEach(([event, handler]) => {
		bindEventWithChunking(`${prefix}\\${event}`, handler)
	})
}

export const bindAppEvents = (eventsByName) => {
	bindEvents('App\\Events', eventsByName)
}

export const bindClientEvents = (eventsByName) => {
	bindEvents('client-App\\Events', eventsByName)
}

export const connectToPusher = () => {
	const { buildNumber, device_type } = window.appConfig
	const version = `${device_type || 'WEB'}/${buildNumber || 1}/0`
	const instance = new Pusher(window.appConfig.pusherAppKey, {
		cluster: 'mt1',
		authEndpoint: '/api/v1/pusher.auth',
		encrypted: true,
		auth: {
			headers: {
				Authorization: `Bearer ${store.auth.api_token}`,
				'X-version': version,
			},
		},
	})
	instance.connection?.bind?.('error', (error) => {
		posthog.capture('pusher_connection_error', error)
		log('connection error', error, localStorage?.pusherTransportTLS)
	})
	instance.connection?.bind?.('state_change', (states) => {
		posthog.capture('pusher_state_change', states)
		log('state_change', states, localStorage?.pusherTransportTLS)
	})
	pusherData.instance = instance
	EventBus.$emit('pusherInitialized', instance)
	return instance
}

const pusherData = {}
export const getPusherInstance = () => {
	return pusherData.instance
}

export const PusherPlugin = {
	install: function (app) {
		pusherData.instance = connectToPusher()
		Object.assign(app.config.globalProperties, {
			$pusher: pusherData.instance,
		})
	},
}

export const initPusher = () => {
	//connect to pusher and listen for specific events we need...
	app.use(PusherPlugin)
}

export const forceReloadPusherInstance = () => {
	log('FORCE RELOADING PUSHER INSTANCE...')
	connectToPusher()
	EventBus.$emit('reloadPusherInstance')
	log('FORCE RELOAD OF PUSHER INSTANCE DONE')
}

export const unsubscribeFromChannel = (channelName) => {
	log('unsubscribing from channel', channelName)
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher.unsubscribe(channelName)
	// after unsubscription attempt, check to see if pusher is still hanging on to channel
	const channel = pusher.channel(channelName)
	if (channel) {
		log(
			'tried to unsubscribe but channel is persisting. Now force unsubscribing...',
			channel
		)
		// this is hacky logic to clean up oprhaned channel
		channel.subscribed = true
		channel.subscriptionPending = false
		channel.subscriptionCancelled = false
		pusher.unsubscribe(channel.name)
		log('force unsubscribe complete', channel)
	}
}

export const unsubscribeFromAllPrivateBubbleChannels = () => {
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher
		.allChannels()
		.filter((channel) => channel.name?.startsWith('private-bubble'))
		.map((channel) => channel.name)
		.forEach(unsubscribeFromChannel)
}

export const subscribeToPrivateBubble = async (bubbleId) => {
	return new Promise((resolve, reject) => {
		const chat = getChat(bubbleId)
		const pusher = getPusherInstance()
		if (!chat || !pusher) return reject(null)

		// make sure we aren't still subscribed to another bubble channel
		unsubscribeFromAllPrivateBubbleChannels()

		const { channelcode } = chat
		const channelName = `private-bubble.${bubbleId}.${channelcode}`
		const channel = pusher.subscribe(channelName)

		//unbind any orphaned events
		channel.unbind()

		if (channel.subscribed) {
			return resolve(channel)
		}
		channel.bind('pusher:subscription_succeeded', () => {
			log('private.bubble subscription succeeded')
			resolve(channel)
		})

		channel.bind('pusher:subscription_error', async (error) => {
			log(
				'error subscribing to channel',
				channelName,
				error.status,
				error.error
			)
			if (error.status === 403) {
				// this means one of two possible scenarios:
				//  1. user has been removed from this channel so we need to bail
				//  2. we have a bad cached channelcode, need to fetch the new one
				log('fetching bubble.info to check for channelcode...')
				try {
					// TODO: swap this over to fetchChat
					const { ok, bubble } = await fetchBubble(bubbleId)
					if (!ok) {
						log('error fetching bubble.info')
						reject(error)
					}
					setChat(bubble)
					log('new channelcode:', bubble.channelcode)
					if (bubble.channelcode !== channelcode) {
						log('channel code changed! attempting to resubscribe...')
						try {
							const channel = await subscribeToPrivateBubble(bubbleId)
							log(
								'successfully resubscribed to private bubble channel!',
								bubbleId,
								channelcode
							)
							resolve(channel)
						} catch (e) {
							reject(e)
						}
					} else {
						log('already had correct channelcode, so bailing')
						reject(error)
					}
				} catch (e) {
					console.error(e)
				}
			}
		})
	})
}

const getPresenceChannelName = () => {
	const user = getMyUser()
	if (!user) return null
	return `presence-user.${user.id}`
}

export const getPresenceChannel = () => {
	const channelName = getPresenceChannelName()
	if (!channelName) return null
	const pusher = getPusherInstance()
	return pusher.channel(channelName)
}

export const subscribeToPresence = () => {
	const user = getMyUser()
	const { wrapper, embedded } = store.config
	if (!user || (!user.acceptedtos && (wrapper || embedded))) {
		log('presence', 'no user or no acceptedtos')
		store.userIsActive = false
		return
	}

	const pusher = getPusherInstance()
	let channel = getPresenceChannel()

	if (channel?.subscribed) {
		store.userIsActive = true
		return
	}
	if (channel?.subscriptionPending) return

	const channelName = getPresenceChannelName()
	log('presence', 'not subscribed, attempting to subscribe...', channelName)
	channel = pusher.subscribe(channelName)
	channel.bind('pusher:subscription_succeeded', () => {
		log('presence', 'successfully subscribed')
		store.userIsActive = true
		channel.unbind()
	})
	channel.bind('pusher:subscription_error', (error) => {
		log('presence', 'error during subscription', error)
		channel.unbind()
	})
	log('presence', 'awaiting subscription...', channel)
	store.userIsActive = false
}

export const unsubscribeToPresence = () => {
	const channel = getPresenceChannel()
	if (!channel) return
	log('presence', 'attempting to unsubscribe...')
	unsubscribeFromChannel(channel.name)
	log('presence', 'done unsubscribing')
	store.userIsActive = false
}
